import _merge from 'lodash/merge';
import Store from 'lightningjs-redux';
import { Lightning } from '@lightningjs/sdk';
import { getInitialStyle, colors, defaultContext } from './parentalCode.style';
import { Button } from '../../tornado/button/button.component';
import { t } from '../../i18n';
import { Password } from '../kidsLeavePopup/password/password.component';
import { BrandIcon } from '../../tornado/brand-icon/brandIcon.component';
import KeyboardNumbers from '../../tornado/keyboard/keyboardNumbers/keyboardNumbers.component';
import { BACKSPACE, LETTER_SELECTED_SIGNAL } from '../../tornado/keyboard/keyboard.constants';
import { passwordCharacters, PASSWORD_LENGTH, EXCEPTION_INTERVAL } from './parentalCode.constants';
import { HIDE_POPUP_SIGNAL, ROUTE_BACK_SIGNAL } from '../../popupContainer/popupContainer.constants';
import { createItemAction } from '../../cta/cta.actions';
import { checkParentalRestrictionCode, resetParentalLock } from '../../../logic/user/user.actions';
import { ForgotCodeHint } from './hint/forgotCodeHint.component';
import connect from './parentalCode.connector';
import { HIDE_SIGNAL, HOME_SIGNAL } from './hint/forgotCodeHint.constants';
import { navigateToAliasHome } from '../../routes/routes.utils';

export class ParentalCode extends Lightning.Component {
  static _template() {
    return _merge(
      {
        Background: {
          rect: true,
        },
        Input: {
          Button: {
            type: Button,
            label: t('global.back'),
          },
          Content: {
            CustomerIcon: {
              type: BrandIcon,
            },
            Title: {
              text: {
                text: t('parentalCode.instruction'),
              },
            },
            PasswordBlock: {
              type: Password,
            },
            ExceptionWrapper: {
              Exception: {
                text: {
                  text: t('parentalCode.incorrectCode'),
                },
              },
            },
            Keyboard: {
              type: KeyboardNumbers,
              signals: {
                [LETTER_SELECTED_SIGNAL]: '_handleSelectedLetter',
              },
            },
            ForgotCodeButton: {
              type: Button,
              label: t('parentalCode.forgottenCode'),
            },
          },
        },
        ForgotCodeHint: {
          type: ForgotCodeHint,
          signals: {
            [HOME_SIGNAL]: '_handleHomeSignal',
            [HIDE_SIGNAL]: '_handleHideSignal',
          },
        },
        passSignals: {
          [ROUTE_BACK_SIGNAL]: true,
          [HIDE_POPUP_SIGNAL]: true,
        },
      },
      getInitialStyle(),
    );
  }

  static _states() {
    return [
      class Button extends this {
        _getFocused() {
          return this.tag('Button');
        }

        _handleEnter() {
          this.signal(this._routeBackOnClose ? ROUTE_BACK_SIGNAL : HIDE_POPUP_SIGNAL);

          return true;
        }

        _handleDown() {
          this._setState('Keyboard');

          return true;
        }
      },
      class Keyboard extends this {
        _getFocused() {
          return this.tag('Keyboard');
        }

        _handleUp() {
          this._setState('Button');

          return true;
        }

        _handleDown() {
          this._setState('ForgotCode');

          return true;
        }
      },
      class ForgotCode extends this {
        static _states() {
          return [
            class Open extends this {
              $enter() {
                this.tag('ForgotCodeHint').visible = true;
              }

              $exit() {
                this.tag('ForgotCodeHint').visible = false;
              }

              _getFocused() {
                return this.tag('ForgotCodeHint');
              }

              _handleBack() {
                this._handleHideSignal();

                return true;
              }

              _handleHomeSignal() {
                navigateToAliasHome();
                this.signal(HIDE_POPUP_SIGNAL);
              }

              _handleHideSignal() {
                this._setState('ForgotCode');
              }
            },
          ];
        }

        _getFocused() {
          return this.tag('ForgotCodeButton');
        }

        _handleEnter() {
          this._setState('ForgotCode.Open');

          return true;
        }

        _handleUp() {
          this._setState('Keyboard');

          return true;
        }
      },
    ];
  }

  set routeBackOnClose(value) {
    this._routeBackOnClose = value;
  }

  _init() {
    this._style = defaultContext;
    this.password = '';
    this._exceptionInterval = null;

    const keyboard = this.tag('Keyboard');
    keyboard.itemsPerRow = passwordCharacters.length;
    keyboard.renderKeyboard(passwordCharacters);
    this.tag('PasswordBlock').cells = PASSWORD_LENGTH;
    this._setState('Keyboard');
  }

  _attach() {
    this._unsubscribe = connect(this);
  }

  _detach() {
    this._clearExceptionInterval();
    this._cleanup();
  }

  _cleanup() {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }
    Store.dispatch(resetParentalLock());
  }

  set payload(value) {
    this._payload = value;
  }

  get isEmptyPassword() {
    return this.password === '';
  }

  get isAllPassword() {
    return this.password.length === PASSWORD_LENGTH;
  }

  handlePopup(value) {
    if (value) {
      this.signal(HIDE_POPUP_SIGNAL);
      Store.dispatch(createItemAction(this._payload));

      return;
    }

    // NOTE: don't show the exception message for the first time this pop-up is opened
    if (value === false) {
      this._showException();
      this._clearExceptionInterval();
      this._exceptionInterval = setTimeout(() => this._hideException(), EXCEPTION_INTERVAL);
    }
  }

  _handleSelectedLetter(letter) {
    if (this.isAllPassword) {
      return;
    }

    if (this.isEmptyPassword) {
      this._resetPasswordView();
    }

    this._updatePassword(letter);
  }

  _updatePassword(value) {
    switch (value) {
      case BACKSPACE: {
        this.password = this.password.slice(0, this.password.length ? this.password.length - 1 : 0);
        this.tag('PasswordBlock').pop();

        break;
      }

      default: {
        this.password += value;
        this.tag('PasswordBlock').push(value);

        if (this.isAllPassword) {
          Store.dispatch(checkParentalRestrictionCode(this.password));
        }
      }
    }
  }

  _clearExceptionInterval() {
    if (this._exceptionInterval) {
      clearTimeout(this._exceptionInterval);
    }
  }

  _resetPasswordView() {
    this._hideException();
  }

  _handleBack() {
    this.signal(this._routeBackOnClose ? ROUTE_BACK_SIGNAL : HIDE_POPUP_SIGNAL);

    return true;
  }

  _showException() {
    this.patch({
      Input: {
        Content: {
          PasswordBlock: {
            borderCellColor: {
              color: colors.exception[this._style.colorMode],
              opacity: 1,
            },
          },
        },
      },
    });
    this.tag('Exception').alpha = 1;
  }

  _hideException() {
    this.patch({
      Input: {
        Content: {
          PasswordBlock: {
            borderCellColor: {
              color: colors.border[this._style.colorMode],
              opacity: 0.3,
            },
          },
        },
      },
    });
    this.tag('PasswordBlock').clearPasswordInput();
    this.tag('Exception').alpha = 0;
    this.password = '';
    this._clearExceptionInterval();
  }
}
